import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { errorBox, inputClass } from '../AuthLayout';

const PAGE = 50;
const SOURCES = { auth: 'Account', invite: 'Company invite', test: 'Test', system: 'System' };
const formatWhen = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** Where an email got to: our send, then what Resend reported back (resend-webhook). */
function deliveryState(r) {
  if (r.status !== 'sent') return { label: 'Failed', tone: 'bad' };
  if (r.delivery_status === 'bounced') return { label: 'Bounced', tone: 'bad' };
  if (r.delivery_status === 'complained') return { label: 'Marked as spam', tone: 'bad' };
  if (r.clicked_at) return { label: 'Clicked', tone: 'great' };
  if (r.opened_at) return { label: 'Opened', tone: 'great' };
  if (r.delivery_status === 'delivered') return { label: 'Delivered', tone: 'good' };
  if (r.delivery_status === 'delayed') return { label: 'Delayed', tone: 'wait' };
  return { label: 'Sent', tone: 'sent' };
}
const TONES = {
  bad: ['bg-coral-soft text-coral-ink', 'bg-coral-ink'],
  great: ['bg-blue text-white', 'bg-white'],
  good: ['bg-blue-soft text-blue', 'bg-blue'],
  wait: ['bg-surface text-ink border border-line-strong', 'bg-coral'],
  sent: ['bg-surface text-ink-muted border border-line', 'bg-line-strong'],
};

function StatusChip({ row }) {
  const { label, tone } = deliveryState(row);
  const [chip, dot] = TONES[tone];
  return (
    <span className={`h-6 px-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap ${chip}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/** Admin console: every email Carduzo has sent, newest first. */
export default function EmailLog() {
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [template, setTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    supabase.from('email_templates').select('key, name').order('sort_order')
      .then(({ data }) => setTemplates((data || []).filter((t) => t.key !== 'layout')));
  }, []);

  const load = useCallback(async (offset = 0) => {
    setError('');
    let q = supabase
      .from('email_log')
      .select('id, created_at, template_key, to_email, subject, status, error, source, delivery_status, opened_at, clicked_at, organizations(name)')
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE);
    const term = search.trim().replace(/[%,()]/g, '');
    if (term) q = q.or(`to_email.ilike.%${term}%,subject.ilike.%${term}%`);
    if (status === 'sent' || status === 'failed') q = q.eq('status', status);
    if (status === 'delivered') q = q.eq('delivery_status', 'delivered').is('opened_at', null);
    if (status === 'opened') q = q.not('opened_at', 'is', null);
    if (status === 'problem') q = q.in('delivery_status', ['bounced', 'complained']);
    if (status === 'pending') q = q.eq('status', 'sent').is('delivery_status', null);
    if (template) q = q.eq('template_key', template);
    const { data, error } = await q;
    if (error) return setError(error.message);
    setMore(data.length > PAGE);
    const page = data.slice(0, PAGE);
    setRows((prev) => (offset ? [...(prev || []), ...page] : page));
  }, [search, status, template]);

  useEffect(() => {
    const t = setTimeout(() => load(0), 250);
    return () => clearTimeout(t);
  }, [load]);

  const name = (key) => templates.find((t) => t.key === key)?.name || key || '—';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <input type="search" placeholder="Search by email or subject" value={search} onChange={(e) => setSearch(e.target.value)}
          aria-label="Search the email log" className={`${inputClass.replace('w-full ', '')} flex-1 min-w-[220px] max-w-md`} />
        <select value={template} onChange={(e) => setTemplate(e.target.value)} aria-label="Template" className={`${inputClass.replace('w-full ', '')} w-auto`}>
          <option value="">All emails</option>
          {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" className={`${inputClass.replace('w-full ', '')} w-auto`}>
          <option value="">All emails</option>
          <option value="opened">Opened</option>
          <option value="delivered">Delivered, not opened</option>
          <option value="pending">No report yet</option>
          <option value="problem">Bounced or spam</option>
          <option value="failed">Failed to send</option>
        </select>
      </div>

      {error && <div className={errorBox}>{error}</div>}

      <div className="bg-white rounded-2xl border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left">
            <tr className="text-xs font-semibold tracking-wider uppercase text-ink-muted">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">To</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows === null && !error && <tr><td colSpan="4" className="px-4 py-8 text-center text-ink-muted">Loading...</td></tr>}
            {rows?.length === 0 && (
              <tr><td colSpan="4" className="px-4 py-10 text-center text-ink-muted">
                {search || status || template ? 'No emails match.' : 'No emails yet. They appear here as Carduzo sends them.'}
              </td></tr>
            )}
            {rows?.map((r) => (
              <tr key={r.id} onClick={() => setOpen(r.id)} className="cursor-pointer hover:bg-surface">
                <td className="px-4 py-3 whitespace-nowrap text-ink-muted">{formatWhen(r.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-ink break-all">{r.to_email}</div>
                  {r.organizations?.name && <div className="text-xs text-ink-muted">{r.organizations.name}</div>}
                </td>
                <td className="px-4 py-3 min-w-[220px]">
                  <button className="text-left font-medium text-ink hover:text-blue" onClick={(e) => { e.stopPropagation(); setOpen(r.id); }}>
                    {r.subject}
                  </button>
                  <div className="text-xs text-ink-muted">{name(r.template_key)} · {SOURCES[r.source] || r.source}</div>
                </td>
                <td className="px-4 py-3"><StatusChip row={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more && (
        <button onClick={() => load(rows.length)} className="h-10 px-4 rounded-lg border border-line-strong bg-white text-sm font-semibold hover:bg-surface">
          Show more
        </button>
      )}

      {open && <EmailDetail id={open} templateName={name} onClose={() => setOpen(null)} />}
    </div>
  );
}

function EmailDetail({ id, templateName, onClose }) {
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('email_log').select('*, organizations(name)').eq('id', id).single()
      .then(({ data, error }) => (error ? setError(error.message) : setRow(data)));
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-navy/40" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label="Email details" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-line">
          <div className="min-w-0">
            <h2 className="text-xl font-bold break-words">{row?.subject || 'Email'}</h2>
            {row && <p className="text-sm text-ink-muted mt-1">{templateName(row.template_key)} · {formatWhen(row.created_at)}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="w-10 h-10 shrink-0 rounded-lg hover:bg-surface flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        {error && <div className={`${errorBox} m-6`}>{error}</div>}
        {row && (
          <div className="flex-1 overflow-y-auto">
            <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 px-6 py-4 text-sm border-b border-line">
              <dt className="text-ink-muted">To</dt><dd className="break-all">{row.to_email}</dd>
              <dt className="text-ink-muted">Status</dt><dd><StatusChip row={row} /></dd>
              {row.delivered_at && (<><dt className="text-ink-muted">Delivered</dt><dd>{formatWhen(row.delivered_at)}</dd></>)}
              {row.opened_at && (<><dt className="text-ink-muted">First opened</dt><dd>{formatWhen(row.opened_at)}</dd></>)}
              {row.clicked_at && (<><dt className="text-ink-muted">First click</dt><dd>{formatWhen(row.clicked_at)}</dd></>)}
              {row.bounced_at && (<><dt className="text-ink-muted">Bounced</dt><dd>{formatWhen(row.bounced_at)}</dd></>)}
              {row.delivery_note && (<><dt className="text-ink-muted">Details</dt><dd className="text-coral-ink break-words">{row.delivery_note}</dd></>)}
              {row.error && (<><dt className="text-ink-muted">Error</dt><dd className="text-coral-ink break-words">{row.error}</dd></>)}
              <dt className="text-ink-muted">Sent by</dt><dd>{SOURCES[row.source] || row.source}</dd>
              {row.organizations?.name && (<><dt className="text-ink-muted">Company</dt><dd>{row.organizations.name}</dd></>)}
              {row.provider_id && (<><dt className="text-ink-muted">Resend ID</dt><dd className="font-mono text-xs break-all">{row.provider_id}</dd></>)}
            </dl>
            <p className="px-6 pt-4 text-xs text-ink-muted">Links and codes are hidden in the log, so they can't be reused from here.</p>
            <div className="p-4">
              {row.html
                ? <iframe title="Email as sent" sandbox="" srcDoc={row.html} className="w-full h-[640px] rounded-lg border border-line bg-white" />
                : <p className="text-ink-muted text-sm px-2">No copy of this email was kept.</p>}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
